import { isSome, Maybe } from '@crocodille/util';

import {
  REQUIRED_VALIDATOR_V2,
  useForm,
  useFormField,
  UseFormState,
  useValidator,
} from '../modules/forms';

const MIN_SCORE = 1;
const MAX_SCORE = 4;

const SCORE_VALIDATOR = (value: Maybe<number>) => {
  if (isSome(value) && (value < MIN_SCORE || value > MAX_SCORE)) {
    return 'validation_error.score' as const;
  }

  return null;
};

export interface OrderScoreFormData {
  readonly comment: string;
  readonly productScore: Maybe<number>;
  readonly deliveryScore: Maybe<number>;
}

export interface OrderScoreFormOptions {
  readonly isCourierDelivery: boolean;
  onSubmit?(data: OrderScoreFormData): void;
  readonly initialScore?: {
    readonly product?: Maybe<number>;
    readonly delivery?: Maybe<number>;
  };
}

export function useOrderScoreForm(
  { onSubmit, isCourierDelivery, initialScore }: OrderScoreFormOptions,
  state?: UseFormState,
) {
  const productScore = useFormField({
    value: initialScore?.product as Maybe<number>,
    required: true,
    validators: [REQUIRED_VALIDATOR_V2, SCORE_VALIDATOR],
  });

  const comment = useFormField({
    value: '',
    required: false,
  });

  const requiredIfCourierDeliveryValidator = useValidator(
    (value) => {
      if (isCourierDelivery) {
        return REQUIRED_VALIDATOR_V2(value);
      } else {
        return null;
      }
    },
    [isCourierDelivery],
  );

  const deliveryScore = useFormField({
    value: initialScore?.delivery as Maybe<number>,
    required: isCourierDelivery,
    validators: [requiredIfCourierDeliveryValidator, SCORE_VALIDATOR],
  });

  return useForm(
    {
      onSubmit,
      fields: {
        productScore,
        comment,
        deliveryScore,
      },
    },
    state,
  );
}
